import moment from 'moment-timezone'
import { addParamsToLocation } from '@smarttransit/common-client'
import { initBreadcrumbs, addAlert } from '@/utilities/helpers'

import {
  findExclusionPeriods,
  findTotalExclusionPeriods
} from '@/services/revenue-exclusion-period-service'

function parseBooleanQueryValue (value) {
  if (value === 'true' || value === true) {
    return 'active'
  }
  if (value === 'false' || value === false) {
    return 'inactive'
  }
  return 'all'
}

export default {
  name: 'advanced-revenue-exclusion-periods',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      breadcrumbs: [],
      apiInProgress: false,
      records: [],
      totalRecords: 0,
      filters: {
        agencyId: null,
        recurrenceType: null,
        isActive: 'all',
        dateFrom: null,
        dateTo: null,
        searchKeywords: null
      },
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateUpdated'
      },
      headers: [
        { text: 'Agency ID', align: 'left', sortable: true, value: 'agencyId' },
        { text: 'Recurrence Type', align: 'left', sortable: true, value: 'recurrenceType' },
        { text: 'Period / Pattern', align: 'left', sortable: false, value: 'pattern' },
        { text: 'Reason', align: 'left', sortable: true, value: 'reason' },
        { text: 'Status', align: 'left', sortable: true, value: 'isActive' },
        { text: 'Date Updated', align: 'left', sortable: true, value: 'dateUpdated' }
      ]
    }
  },
  computed: {
    agencyOptions () {
      return this.$store.getters.getAgencies || []
    },
    recurrenceTypeOptions () {
      return ['once', 'weekly', 'monthly', 'yearly']
    },
    activeStatusOptions () {
      return [
        { text: 'All', value: 'all' },
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' }
      ]
    }
  },
  watch: {
    $route () {
      this.breadcrumbs = initBreadcrumbs.bind(this)()
      if (!this.isListRouteActive()) {
        return
      }
      this.loadFromQueryParams()
      this.searchRecords()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.breadcrumbs = initBreadcrumbs.bind(this)()
      if (!this.isListRouteActive()) {
        return
      }
      this.loadFromQueryParams()
      this.searchRecords()
    })
  },
  methods: {
    isListRouteActive () {
      return this.$route && this.$route.name === 'advanced-revenue-exclusion-periods'
    },
    loadFromQueryParams () {
      const query = this.$route.query || {}

      this.filters.agencyId = query.agencyId || null
      this.filters.recurrenceType = query.recurrenceType || null
      this.filters.isActive = parseBooleanQueryValue(query.isActive)
      this.filters.dateFrom = query.dateFrom || null
      this.filters.dateTo = query.dateTo || null
      this.filters.searchKeywords = query.searchKeywords || null

      this.pagination.descending = query.descending !== undefined ? String(query.descending) === 'true' : true
      this.pagination.page = query.page ? Number(query.page) : 1
      this.pagination.rowsPerPage = query.rowsPerPage ? Number(query.rowsPerPage) : 10
      this.pagination.sortBy = query.sortBy || 'dateUpdated'
    },
    updateQueryParams () {
      const queryParams = {
        agencyId: this.filters.agencyId || null,
        recurrenceType: this.filters.recurrenceType || null,
        isActive: this.filters.isActive === 'all' ? null : String(this.filters.isActive === 'active'),
        dateFrom: this.filters.dateFrom || null,
        dateTo: this.filters.dateTo || null,
        searchKeywords: this.filters.searchKeywords || null,
        descending: this.pagination.descending,
        page: this.pagination.page,
        rowsPerPage: this.pagination.rowsPerPage,
        sortBy: this.pagination.sortBy || null
      }

      this.lastParams = addParamsToLocation(queryParams, this.$route, this.lastParams)
    },
    buildWhereFilter () {
      const andFilter = []

      if (this.filters.agencyId) {
        andFilter.push({ agencyId: this.filters.agencyId })
      }

      if (this.filters.recurrenceType) {
        andFilter.push({ recurrenceType: this.filters.recurrenceType })
      }

      if (this.filters.isActive === 'active') {
        andFilter.push({ isActive: true })
      } else if (this.filters.isActive === 'inactive') {
        andFilter.push({ isActive: false })
      }

      if (this.filters.searchKeywords) {
        andFilter.push({
          reason: {
            ilike: '%' + this.filters.searchKeywords + '%'
          }
        })
      }

      const periodStart = this.filters.dateFrom ? moment.utc(this.filters.dateFrom, 'YYYY-MM-DD', true).startOf('day').toISOString() : null
      const periodEnd = this.filters.dateTo ? moment.utc(this.filters.dateTo, 'YYYY-MM-DD', true).endOf('day').toISOString() : null

      if (periodStart) {
        andFilter.push({
          or: [
            { periodEnd: null },
            { periodEnd: { gte: periodStart } }
          ]
        })
      }

      if (periodEnd) {
        andFilter.push({
          or: [
            { periodStart: null },
            { periodStart: { lte: periodEnd } }
          ]
        })
      }

      if (!andFilter.length) {
        return {}
      }

      return {
        and: andFilter
      }
    },
    async searchRecords () {
      if (!this.isListRouteActive()) {
        return
      }

      this.apiInProgress = true

      try {
        const page = this.pagination.page || 1
        const rowsPerPage = this.pagination.rowsPerPage || 10
        const offset = (page - 1) * rowsPerPage
        const sortDirection = this.pagination.descending ? 'DESC' : 'ASC'
        const sortBy = this.pagination.sortBy || 'dateUpdated'
        const where = this.buildWhereFilter()
        const filter = {
          where,
          limit: rowsPerPage,
          offset,
          order: sortBy + ' ' + sortDirection
        }

        const result = await Promise.all([
          findExclusionPeriods(filter),
          findTotalExclusionPeriods({ where })
        ])

        this.records = result[0] || []
        this.totalRecords = result[1] && result[1].count ? Number(result[1].count) : 0
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to load exclusion periods',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    formatDateTime (value) {
      if (!value) {
        return '-'
      }
      return moment.utc(value).format('YYYY-MM-DD HH:mm:ss')
    },
    formatRecurrenceType (value) {
      if (!value) {
        return '-'
      }
      return String(value).replace('-', ' ').replace(/\b\w/g, (s) => s.toUpperCase())
    },
    formatPattern (item) {
      if (!item) {
        return '-'
      }

      const recurrenceType = item.recurrenceType || ''
      if (recurrenceType === 'once') {
        return (item.periodStart ? moment.utc(item.periodStart).format('YYYY-MM-DD') : '-') +
          ' -> ' +
          (item.periodEnd ? moment.utc(item.periodEnd).format('YYYY-MM-DD') : '-')
      }

      if (recurrenceType === 'weekly') {
        return 'weekday ' + (item.dayOfWeekStart !== null && item.dayOfWeekStart !== undefined ? item.dayOfWeekStart : '-') +
          (item.dayOfWeekEnd !== null && item.dayOfWeekEnd !== undefined ? ' to ' + item.dayOfWeekEnd : '')
      }

      if (recurrenceType === 'monthly') {
        return 'day ' + (item.dayOfMonthStart || '-') + (item.dayOfMonthEnd ? ' to ' + item.dayOfMonthEnd : '')
      }

      if (recurrenceType === 'yearly') {
        if (item.holidayKey) {
          return 'holiday: ' + item.holidayKey
        }
        const monthLabel = item.monthStart || '-'
        const dayLabel = item.dayOfMonthStart || '-'
        return monthLabel + '/' + dayLabel
      }

      return '-'
    },
    openRecord (item) {
      if (!item || !item.id) {
        return
      }
      this.$router.push({
        name: 'advanced-revenue-exclusion-periods-period',
        params: { exclusionPeriodId: item.id }
      })
    },
    async onApplyFilters () {
      this.pagination.page = 1
      this.updateQueryParams()
      await this.searchRecords()
    },
    async onPagination () {
      this.updateQueryParams()
      await this.searchRecords()
    },
    async resetFilters () {
      this.filters = {
        agencyId: null,
        recurrenceType: null,
        isActive: 'all',
        dateFrom: null,
        dateTo: null,
        searchKeywords: null
      }
      this.pagination.page = 1
      this.updateQueryParams()
      await this.searchRecords()
    }
  }
}
